/**
 * ContractorOnboarding — Integration Pass (Sprint A.1 + UI Integration)
 * ─────────────────────────────────────────────────────────────────────
 * Primary onboarding lifecycle cockpit. Lifecycle is projected from
 * OnboardingSession + InviteToken via the canonical lifecycle resolver.
 * Contractor entity records are NEVER consulted for lifecycle stage.
 *
 * Legacy / Existing Contractors:
 *   Any contractor with no linked OnboardingSession is shown in a
 *   collapsed "Existing Contractors" section so seeded/older records
 *   do not dominate the cockpit. Labelled explicitly as legacy. We
 *   never synthesize a session for them.
 *
 * Hard rules:
 *   • No direct session.status mutation here.
 *   • All lifecycle actions deep-link to the wizard or to Compliance
 *     Review (which routes through `advanceOnboardingSession`).
 *   • No payout execution, no dispatch unlock, no portal unlock.
 */

import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { UNIFIED_ONBOARDING_COCKPIT } from "@/config/onboardingCockpitFlags";
import { useAppData } from "@/data/AppDataContext";
import { ContractorService } from "@/services/contractor.service";
import type { Contractor } from "@/data/types";
import UnifiedReviewerPanel from "@/components/onboarding-cockpit/UnifiedReviewerPanel";
import { PaginationBar } from "@/components/PaginationBar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PermissionGate } from "@/components/PermissionGate";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search, Building2, Phone, MapPin, ShieldCheck, FileCheck, Inbox,
  Mail, Clock, FileEdit, Upload, Eye, CheckCircle2, XCircle, AlertTriangle, Archive,
  ChevronDown, ChevronRight, Copy, ExternalLink, Link2, Trash2,
} from "lucide-react";
import { usePagination } from "@/hooks/usePagination";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import type {
  OnboardingInviteToken,
  OnboardingSession,
} from "@/data/contractor/onboarding/types";
import {
  getOnboardingLifecycleRows,
  getOnboardingLifecycleStageCounts,
  ONBOARDING_LIFECYCLE_LABELS,
  ONBOARDING_LIFECYCLE_STAGES,
  type OnboardingLifecycleStage,
} from "@/data/contractor/onboarding/lifecycle";
import { getOnboardingSessionDocSummary } from "@/data/contractor/onboarding/adminReview";
import { useToast } from "@/hooks/use-toast";

type LifecycleRow = ReturnType<typeof getOnboardingLifecycleRows>[number];

const STAGE_ICONS: Record<OnboardingLifecycleStage, React.ElementType> = {
  invited: Mail,
  in_progress: FileEdit,
  submitted: Upload,
  reupload_requested: Clock,
  under_review: Eye,
  approved: CheckCircle2,
  rejected: XCircle,
  expired: AlertTriangle,
  withdrawn: Archive,
};

const STAGE_TONE: Record<OnboardingLifecycleStage, string> = {
  invited: "bg-info/15 text-info border-info/30",
  in_progress: "bg-warning/15 text-warning border-warning/30",
  submitted: "bg-primary/15 text-primary border-primary/30",
  reupload_requested: "bg-warning/15 text-warning border-warning/30",
  under_review: "bg-primary/15 text-primary border-primary/30",
  approved: "bg-success/15 text-success border-success/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  expired: "bg-muted text-muted-foreground border-border",
  withdrawn: "bg-muted text-muted-foreground border-border line-through",
};

function compact(value: unknown): string {
  return String(value ?? "").trim();
}

function shortId(value: string | undefined): string {
  return compact(value).slice(-6) || "new";
}

function getServiceCoverageParts(
  contractor: Contractor | undefined,
  draft: OnboardingSession["draft"] | undefined,
): { serviceText: string; regions: string[]; zips: string[] } {
  const regions = contractor?.serviceRegions?.length
    ? contractor.serviceRegions
    : Array.isArray(draft?.serviceRegions)
      ? draft.serviceRegions.map(compact).filter(Boolean)
      : [];
  const zips = contractor?.serviceZipCodes?.length
    ? contractor.serviceZipCodes
    : Array.isArray(draft?.serviceZipCodes)
      ? draft.serviceZipCodes.map(compact).filter(Boolean)
      : [];
  const serviceArea = compact(contractor?.serviceArea);
  const serviceText = serviceArea || regions.join(", ") || zips.join(", ");
  return { serviceText, regions, zips };
}

function summarizeServiceCoverage(
  serviceText: string,
  regions: string[],
  zips: string[],
): string {
  if (regions.length > 0 && zips.length > 0) {
    return `${regions.slice(0, 2).join(", ")} + ${zips.length} ZIPs`;
  }
  if (regions.length > 0) {
    return regions.length > 2 ? `${regions.slice(0, 2).join(", ")} + ${regions.length - 2} more` : regions.join(", ");
  }
  if (zips.length > 0) {
    const preview = zips.slice(0, 3).join(", ");
    return zips.length > 3 ? `${preview} + ${zips.length - 3} more` : preview;
  }
  return serviceText;
}

function buildApplicationLabel(
  row: LifecycleRow,
  session: OnboardingSession | undefined,
  token: OnboardingInviteToken | undefined,
  contractor: Contractor | undefined,
) {
  const draft = session?.draft;
  const companyName = compact(contractor?.companyName) || compact(draft?.name);
  const contactName = compact(contractor?.contactName) || compact(draft?.contactName);
  const email = compact(contractor?.email) || compact(draft?.contactEmail);
  const phone = compact(contractor?.phone) || compact(draft?.contactPhone);
  const trade = compact(contractor?.tradeType) || compact(draft?.tradeType);
  const { serviceText, regions, zips } = getServiceCoverageParts(contractor, draft);
  const serviceArea = compact(serviceText);
  const serviceSummary = summarizeServiceCoverage(serviceArea, regions, zips);
  const primary = companyName || contactName || email || row.label;
  const subtitleParts = [contactName && contactName !== primary ? contactName : "", email, phone]
    .map(compact)
    .filter(Boolean);
  const metaParts = [
    `Entity: ${row.entityType}`,
    trade ? `Trade: ${trade}` : "",
    serviceSummary ? `Service: ${serviceSummary}` : "",
  ].filter(Boolean);
  const applicationRef = row.sessionId
    ? `Application ${shortId(row.sessionId)}`
    : `Invite ${shortId(token?.id ?? row.tokenId)}`;

  return {
    primary,
    subtitle: subtitleParts.join(" · "),
    metaParts,
    applicationRef,
    serviceArea,
    serviceSummary,
    serviceRegions: regions,
    serviceZipCodes: zips,
    searchText: [
      primary,
      row.label,
      contactName,
      email,
      phone,
      trade,
      serviceArea,
      row.entityType,
      row.sessionId,
      row.tokenId,
      contractor?.id,
    ].map(compact).join(" ").toLowerCase(),
  };
}

export default function ContractorOnboarding() {
  useAppData(); // keep provider mounted
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const onboarding = useOnboarding();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const selectedSessionId = UNIFIED_ONBOARDING_COCKPIT ? params.get("session") : null;
  const selectedTokenId = UNIFIED_ONBOARDING_COCKPIT ? params.get("token") : null;

  useEffect(() => {
    ContractorService.fetchContractors().then(setContractors).catch(console.warn);
    return ContractorService.subscribeToContractors(setContractors);
  }, []);

  const lifecycleRows = useMemo(
    () => getOnboardingLifecycleRows(onboarding.inviteTokens, onboarding.sessions, { perspective: "admin" }),
    [onboarding.inviteTokens, onboarding.sessions],
  );
  const stageCounts = useMemo(
    () => getOnboardingLifecycleStageCounts(lifecycleRows),
    [lifecycleRows],
  );

  const [stageFilter, setStageFilter] = useState<OnboardingLifecycleStage | "All">("All");
  const [search, setSearch] = useState("");
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [serviceAreaDialog, setServiceAreaDialog] = useState<{
    title: string;
    regions: string[];
    zips: string[];
    raw: string;
  } | null>(null);

  const contractorById = useMemo(
    () => new Map(contractors.map((contractor) => [contractor.id, contractor])),
    [contractors],
  );
  const sessionById = useMemo(
    () => new Map(onboarding.sessions.map((session) => [session.id, session])),
    [onboarding.sessions],
  );
  const tokenById = useMemo(
    () => new Map(onboarding.inviteTokens.map((token) => [token.id, token])),
    [onboarding.inviteTokens],
  );

  const filteredRows = useMemo(() => {
    return lifecycleRows.filter((r) => {
      if (stageFilter !== "All" && r.stage !== stageFilter) return false;
      if (stageFilter === "All" && r.stage === "withdrawn") return false;
      if (search) {
        const q = search.toLowerCase();
        const session = r.sessionId ? sessionById.get(r.sessionId) : undefined;
        const token = tokenById.get(r.tokenId);
        const contractor = r.contractorId ? contractorById.get(r.contractorId) : undefined;
        const label = buildApplicationLabel(r, session, token, contractor);
        if (!label.searchText.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [contractorById, lifecycleRows, search, sessionById, stageFilter, tokenById]);
  const lifecyclePagination = usePagination(filteredRows, 10);

  // Broad definition (Sprint A.1 lock): legacy = any contractor record
  // with no linked OnboardingSession. We never synthesize a session.
  const sessionContractorIds = useMemo(
    () =>
      new Set(
        onboarding.sessions
          .map((s) => s.contractorId)
          .filter((id): id is string => Boolean(id)),
      ),
    [onboarding.sessions],
  );
  const legacyContractors = useMemo(
    () => contractors.filter((c) => !sessionContractorIds.has(c.id)),
    [contractors, sessionContractorIds],
  );
  const filteredLegacy = useMemo(() => {
    if (!search) return legacyContractors;
    const q = search.toLowerCase();
    return legacyContractors.filter((c) =>
      c.companyName.toLowerCase().includes(q) ||
      c.contactName.toLowerCase().includes(q) ||
      c.tradeType.toLowerCase().includes(q),
    );
  }, [legacyContractors, search]);
  const legacyPagination = usePagination(filteredLegacy, 15);

  function copyInviteUrl(tokenId: string) {
    const token = onboarding.inviteTokens.find((t) => t.id === tokenId);
    if (!token) return;
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/onboarding/${encodeURIComponent(token.token)}`;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => undefined);
    }
    toast({ title: "Invite link copied", description: url });
  }

  function openSessionReviewer(sessionId: string) {
    const next = new URLSearchParams(params);
    next.set("session", sessionId);
    next.delete("token");
    next.delete("noAuto");
    setParams(next, { replace: true });
  }

  function openTokenReviewer(tokenId: string) {
    const next = new URLSearchParams(params);
    next.set("token", tokenId);
    next.delete("session");
    next.delete("noAuto");
    setParams(next, { replace: true });
  }

  function formatAccessDeactivationSummary(result: {
    crewMembershipsSuspended: number;
    profilesDeactivated: number;
    warnings: string[];
  }) {
    const parts = [
      result.crewMembershipsSuspended > 0
        ? `${result.crewMembershipsSuspended} portal access membership${result.crewMembershipsSuspended === 1 ? "" : "s"} suspended`
        : "",
      result.profilesDeactivated > 0
        ? `${result.profilesDeactivated} profile${result.profilesDeactivated === 1 ? "" : "s"} deactivated`
        : "",
      result.warnings.length > 0 ? `${result.warnings.length} access warning${result.warnings.length === 1 ? "" : "s"}` : "",
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : "No connected portal users were found.";
  }

  async function archiveEntry(row: LifecycleRow) {
    const label = row.sessionId ? "application" : "invite";
    const confirmed = window.confirm(
      `Archive this onboarding ${label}? It will move to Withdrawn and stay available for audit history.`,
    );
    if (!confirmed) return;

    try {
      const accessResult = await onboarding.archiveOnboardingEntry(
        { sessionId: row.sessionId, tokenId: row.tokenId },
        "admin",
        "Archived from contractor onboarding queue",
      );
      toast({
        title: "Onboarding entry archived",
        description: formatAccessDeactivationSummary(accessResult),
      });
    } catch (error) {
      toast({
        title: "Could not archive entry",
        description: error instanceof Error ? error.message : "Archive failed.",
        variant: "destructive",
      });
    }
  }

  async function deleteEntry(row: LifecycleRow) {
    const label = row.sessionId ? "application" : "invite";
    const confirmed = window.confirm(
      `Delete this onboarding ${label} from the active queue? This is a soft delete for audit safety.`,
    );
    if (!confirmed) return;

    try {
      const accessResult = await onboarding.deleteOnboardingEntry(
        { sessionId: row.sessionId, tokenId: row.tokenId },
        "admin",
        "Deleted from contractor onboarding queue",
      );
      toast({
        title: "Onboarding entry deleted",
        description: formatAccessDeactivationSummary(accessResult),
      });
    } catch (error) {
      toast({
        title: "Could not delete entry",
        description: error instanceof Error ? error.message : "Delete failed.",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="space-y-6">
      {/* Helper context — title lives in the masthead above */}
      <p className="text-sm text-muted-foreground -mt-2">
        Canonical onboarding lifecycle — projected from active sessions and invites.
        Lifecycle advances through the wizard and{" "}
        <Link to="/admin/compliance-review" className="text-primary hover:underline">
          Compliance Review
        </Link>
        .
      </p>

      {/* Lifecycle stage ribbon — wrapping chips, label + count. */}
      <div
        className="flex flex-wrap gap-2"
        data-testid="onboarding-lifecycle-strip"
      >
        {ONBOARDING_LIFECYCLE_STAGES.map((stage) => {
          const Icon = STAGE_ICONS[stage];
          const isActive = stageFilter === stage;
          const count = stageCounts[stage];
          return (
            <button
              key={stage}
              data-testid={`lifecycle-stage-${stage}`}
              onClick={() => setStageFilter(isActive ? "All" : stage)}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                isActive
                  ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-primary/30"
                  : "border-border bg-card/40 text-muted-foreground hover:text-foreground hover:border-border/80"
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="font-medium whitespace-nowrap">
                {ONBOARDING_LIFECYCLE_LABELS[stage]}
              </span>
              <span className={`tabular-nums font-bold ${isActive ? "text-primary" : "text-foreground"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + active-filter affordance */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sessions or legacy contractors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {(stageFilter !== "All" || search) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setStageFilter("All"); setSearch(""); }}
            data-testid="onboarding-clear-filters"
            className="text-xs text-muted-foreground"
          >
            Showing: {stageFilter === "All" ? "All stages" : ONBOARDING_LIFECYCLE_LABELS[stageFilter]} · Clear
          </Button>
        )}
      </div>

      <div className={UNIFIED_ONBOARDING_COCKPIT ? "grid gap-6 lg:grid-cols-[1.2fr_1fr] items-start" : ""}>
      <div className="min-w-0 space-y-4">
      {/* Canonical lifecycle rows */}
      <div className="space-y-3" data-testid="onboarding-lifecycle-list">
        {filteredRows.length === 0 ? (
          <Card data-testid="onboarding-lifecycle-empty">
            <CardContent className="py-12">
              <div className="flex flex-col items-center text-center max-w-sm mx-auto">
                <div className="h-12 w-12 rounded-full bg-muted grid place-items-center mb-3">
                  <Inbox className="h-5 w-5 text-muted-foreground/70" />
                </div>
                <p className="font-medium text-foreground">
                  {stageFilter === "All" && !search
                    ? "No onboarding activity yet"
                    : "No sessions match your filters"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {stageFilter === "All" && !search
                    ? "Create your first installer invite to kick off the onboarding lifecycle."
                    : "Try clearing the search or selecting a different stage above."}
                </p>
                {(stageFilter !== "All" || search) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setStageFilter("All");
                      setSearch("");
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          lifecyclePagination.paged.map((row) => {
            const Icon = STAGE_ICONS[row.stage];
            const session = row.sessionId
              ? sessionById.get(row.sessionId)
              : undefined;
            const token = tokenById.get(row.tokenId);
            const contractor = row.contractorId ? contractorById.get(row.contractorId) : undefined;
            const label = buildApplicationLabel(row, session, token, contractor);
            const docSummary = session
              ? getOnboardingSessionDocSummary(session, onboarding.complianceDocuments)
              : null;
            const readiness = session
              ? onboarding.getAdminApprovalReadiness(session.id)
              : null;
            const canCopyInvite = row.stage === "invited";
            const selected =
              (session && selectedSessionId === session.id) ||
              (!session && selectedTokenId === row.tokenId);
            return (
              <Card
                key={row.id}
                className="hover:shadow-md transition-shadow"
                data-testid={`lifecycle-row-${row.id}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground truncate">{label.primary}</span>
                        <Badge variant="secondary" className="text-[10px]">
                          {label.applicationRef}
                        </Badge>
                        <Badge variant="outline" className={STAGE_TONE[row.stage]}>
                          <Icon className="h-3 w-3 mr-1" />
                          {ONBOARDING_LIFECYCLE_LABELS[row.stage]}
                        </Badge>
                      </div>
                      {label.subtitle && (
                        <div className="mt-1 text-xs text-muted-foreground truncate">
                          {label.subtitle}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        {label.metaParts.map((part) => (
                          <span key={part}>{part}</span>
                        ))}
                        {label.serviceArea && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs"
                            onClick={() =>
                              setServiceAreaDialog({
                                title: label.primary,
                                regions: label.serviceRegions,
                                zips: label.serviceZipCodes,
                                raw: label.serviceArea,
                              })
                            }
                          >
                            <MapPin className="h-3 w-3 mr-1" />
                            View service area
                          </Button>
                        )}
                        {row.parentEntityId && (
                          <span className="inline-flex items-center gap-1">
                            <Link2 className="h-3 w-3" />
                            Parent: {row.parentEntityId}
                          </span>
                        )}
                        <span>Updated: {new Date(row.lastTouchedAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {canCopyInvite && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyInviteUrl(row.tokenId)}
                          data-testid={`copy-invite-${row.tokenId}`}
                          className="h-7 text-xs"
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy invite
                        </Button>
                      )}
                      {UNIFIED_ONBOARDING_COCKPIT ? (
                        session ? (
                          <Button
                            size="sm"
                            variant={selected ? "default" : "outline"}
                            onClick={() => openSessionReviewer(session.id)}
                            data-testid={`open-reviewer-${session.id}`}
                            className="h-7 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            {selected ? "Viewing" : "View"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant={selected ? "default" : "outline"}
                            onClick={() => openTokenReviewer(row.tokenId)}
                            data-testid={`open-reviewer-${row.tokenId}`}
                            className="h-7 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            {selected ? "Viewing" : "View"}
                          </Button>
                        )
                      ) : (
                        (row.stage === "submitted" ||
                          row.stage === "under_review" ||
                          row.stage === "reupload_requested") && (
                          <Link
                            to="/admin/compliance-review"
                            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                          >
                            Open review <ExternalLink className="h-3 w-3" />
                          </Link>
                        )
                      )}
                      {row.stage !== "withdrawn" && (
                        <PermissionGate permission="contractors_approve">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => archiveEntry(row)}
                            data-testid={`archive-onboarding-entry-${row.id}`}
                            className="h-7 text-xs"
                          >
                            <Archive className="h-3 w-3 mr-1" />
                            Archive
                          </Button>
                        </PermissionGate>
                      )}
                      <PermissionGate permission="contractors_approve">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteEntry(row)}
                          data-testid={`delete-onboarding-entry-${row.id}`}
                          className="h-7 text-xs text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </PermissionGate>
                    </div>
                  </div>

                  {/* Document summary + readiness */}
                  {(docSummary || readiness) && (
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap pt-2 border-t text-[11px]">
                      {docSummary && (
                        <div
                          className="flex items-center gap-3"
                          data-testid={`lifecycle-row-doc-summary-${row.id}`}
                        >
                          <span className="text-muted-foreground">Docs</span>
                          <span className="text-success">{docSummary.verified} verified</span>
                          <span className="text-warning">{docSummary.pending} pending</span>
                          {docSummary.rejected > 0 && (
                            <span className="text-destructive">{docSummary.rejected} rejected</span>
                          )}
                        </div>
                      )}
                      {readiness && readiness.blockers.length > 0 && (
                        <span className="text-destructive inline-flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {readiness.blockers.length} blocker
                          {readiness.blockers.length === 1 ? "" : "s"}
                        </span>
                      )}
                      {readiness && readiness.warnings.length > 0 && (
                        <span className="text-warning">
                          {readiness.warnings.length} warning
                          {readiness.warnings.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
        {filteredRows.length > 0 && (
          <div className="overflow-hidden rounded-md border bg-card">
            <PaginationBar {...lifecyclePagination} />
          </div>
        )}
      </div>

      {/* Legacy / Existing contractors — collapsed by default */}
      <Card data-testid="legacy-contractors-section" className="border-dashed">
        <CardHeader className="pb-3">
          <button
            type="button"
            onClick={() => setLegacyOpen((v) => !v)}
            className="w-full flex items-center gap-2 text-left"
            data-testid="legacy-contractors-toggle"
          >
            {legacyOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Existing Installers</CardTitle>
            <Badge variant="outline">{filteredLegacy.length}</Badge>
            <span className="ml-auto text-xs text-muted-foreground">
              Existing contractor record · no onboarding session
            </span>
          </button>
        </CardHeader>
        {legacyOpen && (
          <CardContent className="space-y-2">
            {filteredLegacy.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No matching legacy contractor records.
              </p>
            ) : (
              legacyPagination.paged.map((c) => (
                <div
                  key={c.id}
                  data-testid={`legacy-contractor-${c.id}`}
                  className="flex items-center gap-3 rounded-md border bg-background/60 px-3 py-2 text-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate flex items-center gap-2">
                      <Link
                        to={`/contractors/${c.id}`}
                        className="hover:underline"
                      >
                        {c.companyName}
                      </Link>
                      <Badge variant="outline" className="text-[10px]">
                        Legacy
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{c.tradeType}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.serviceArea}</span>
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.contactName}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                    <span className={c.insuranceOnFile ? "text-success" : "text-muted-foreground"}>
                      <ShieldCheck className="h-3.5 w-3.5 inline mr-0.5" />Ins
                    </span>
                    <span className={c.w9OnFile ? "text-success" : "text-muted-foreground"}>
                      <FileCheck className="h-3.5 w-3.5 inline mr-0.5" />W9
                    </span>
                    <span className={c.active ? "text-success" : "text-muted-foreground"}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))
            )}
            {filteredLegacy.length > 0 && (
              <div className="overflow-hidden rounded-md border bg-card">
                <PaginationBar {...legacyPagination} />
              </div>
            )}
          </CardContent>
        )}
      </Card>
      </div>
      {UNIFIED_ONBOARDING_COCKPIT && (
        <div className="min-w-0">
          <UnifiedReviewerPanel />
        </div>
      )}
      </div>

      <Dialog open={!!serviceAreaDialog} onOpenChange={(open) => !open && setServiceAreaDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Service Area</DialogTitle>
            <DialogDescription>
              {serviceAreaDialog?.title ?? "Contractor coverage"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {serviceAreaDialog?.regions.length ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Regions</div>
                <div className="rounded-md border p-3">
                  {serviceAreaDialog.regions.join(", ")}
                </div>
              </div>
            ) : null}
            {serviceAreaDialog?.zips.length ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">ZIP Codes</div>
                <div className="rounded-md border p-3 max-h-64 overflow-y-auto font-mono text-xs leading-6">
                  {serviceAreaDialog.zips.join(", ")}
                </div>
              </div>
            ) : null}
            {!serviceAreaDialog?.regions.length && !serviceAreaDialog?.zips.length && serviceAreaDialog?.raw ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Coverage</div>
                <div className="rounded-md border p-3">{serviceAreaDialog.raw}</div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
